import OneSignal from 'react-onesignal'

const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID || ''
const ONESIGNAL_SAFARI_WEB_ID = process.env.REACT_APP_ONESIGNAL_SAFARI_WEB_ID || ''
const SERVICE_WORKER_PATH = 'onesignal/OneSignalSDKWorker.js'
const SERVICE_WORKER_SCOPE = '/onesignal/'

let initPromise = null
let foregroundListenerRegistered = false

export function isOneSignalConfigured() {
  return Boolean(ONESIGNAL_APP_ID)
}

export function getOneSignalPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported'
  }

  if (!isOneSignalConfigured()) {
    return 'not_configured'
  }

  return Notification.permission
}

function getExternalUserId({ deviceId, externalId, email, userId } = {}) {
  return externalId || deviceId || userId || email || ''
}

function getSubscriptionSnapshot(externalUserId = '') {
  return {
    externalUserId,
    oneSignalUserId: OneSignal.User.onesignalId,
    optedIn: OneSignal.User.PushSubscription.optedIn,
    permission: getOneSignalPermission(),
    subscriptionId: OneSignal.User.PushSubscription.id,
  }
}

function registerForegroundNotificationDisplay() {
  if (foregroundListenerRegistered) {
    return
  }

  foregroundListenerRegistered = true

  OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
    event.notification.display()
  })
}

export async function initOneSignalUser(userContext = {}) {
  if (typeof window === 'undefined') {
    return { configured: false, permission: 'unsupported' }
  }

  if (!isOneSignalConfigured()) {
    return { configured: false, permission: getOneSignalPermission() }
  }

  if (!OneSignal.Notifications.isPushSupported()) {
    return { configured: true, permission: 'unsupported' }
  }

  if (!initPromise) {
    initPromise = OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      allowLocalhostAsSecureOrigin: true,
      notifyButton: {
        enable: false,
      },
      serviceWorkerParam: {
        scope: SERVICE_WORKER_SCOPE,
      },
      serviceWorkerPath: SERVICE_WORKER_PATH,
      safari_web_id: ONESIGNAL_SAFARI_WEB_ID || undefined,
      welcomeNotification: {
        disabled: true,
      },
    }).catch((error) => {
      initPromise = null
      throw error
    })
  }

  await initPromise
  registerForegroundNotificationDisplay()

  const externalUserId = getExternalUserId(userContext)

  if (externalUserId) {
    await OneSignal.login(String(externalUserId))
  }

  return {
    configured: true,
    ...getSubscriptionSnapshot(externalUserId),
  }
}

export async function requestOneSignalPermission(userContext = {}) {
  const result = await initOneSignalUser(userContext)

  if (!result.configured) {
    return result.permission
  }

  if (result.permission === 'granted' || result.permission === 'denied') {
    if (result.permission === 'granted') {
      await OneSignal.User.PushSubscription.optIn()

      if (result.externalUserId) {
        await OneSignal.login(String(result.externalUserId))
      }
    }

    return getSubscriptionSnapshot(result.externalUserId)
  }

  const granted = await OneSignal.Notifications.requestPermission()

  if (granted) {
    await OneSignal.User.PushSubscription.optIn()

    if (result.externalUserId) {
      await OneSignal.login(String(result.externalUserId))
    }
  }

  return getSubscriptionSnapshot(result.externalUserId)
}
